import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

const CartContext = createContext(null);

const STORAGE_KEY = "rdr_cart_v2";

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const addItem = (product, qty = 1) => {
    setItems((prev) => {
      const existing = prev.find((i) => i.variant_id === product.variant_id);
      if (existing) {
        return prev.map((i) =>
          i.variant_id === product.variant_id ? { ...i, qty: i.qty + qty } : i
        );
      }
      return [
        ...prev,
        {
          id: product.id,
          variant_id: product.variant_id,
          slug: product.slug,
          name: product.name,
          price: product.price,
          size: product.size,
          image: product.images?.[0],
          qty,
        },
      ];
    });
    setIsOpen(true);
  };

  const removeItem = (variantId) =>
    setItems((prev) => prev.filter((i) => i.variant_id !== variantId));

  const updateQty = (variantId, qty) => {
    if (qty <= 0) return removeItem(variantId);
    setItems((prev) =>
      prev.map((i) => (i.variant_id === variantId ? { ...i, qty } : i))
    );
  };

  const clearCart = () => setItems([]);

  const { count, subtotal } = useMemo(() => {
    const count = items.reduce((s, i) => s + i.qty, 0);
    const subtotal = items.reduce((s, i) => s + i.qty * i.price, 0);
    return { count, subtotal };
  }, [items]);

  const value = {
    items,
    isOpen,
    setIsOpen,
    addItem,
    removeItem,
    updateQty,
    clearCart,
    count,
    subtotal,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
};
